import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Client-side center-crop of an image file to the given aspect ratio.
 * Preserves original resolution as much as possible; exports as JPEG at 92% quality.
 */
async function cropImageToRatio(file, ratio) {
    if (!ratio || !isFinite(ratio) || ratio <= 0) return file;
    const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
    const img = await new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = reject;
        el.src = dataUrl;
    });
    const currentRatio = img.naturalWidth / img.naturalHeight;
    // If already at target ratio (within 1%), skip the crop and re-encode
    if (Math.abs(currentRatio - ratio) / ratio < 0.01) return file;

    let sw, sh, sx, sy;
    if (currentRatio > ratio) {
        // Too wide — crop left/right
        sh = img.naturalHeight;
        sw = Math.round(sh * ratio);
        sx = Math.round((img.naturalWidth - sw) / 2);
        sy = 0;
    } else {
        // Too tall — crop top/bottom
        sw = img.naturalWidth;
        sh = Math.round(sw / ratio);
        sx = 0;
        sy = Math.round((img.naturalHeight - sh) / 2);
    }
    const canvas = document.createElement("canvas");
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
    if (!blob) return file;
    const baseName = (file.name || "image").replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}-16x9.jpg`, { type: "image/jpeg", lastModified: Date.now() });
}

/**
 * Drag-and-drop image uploader.
 * Uploads to POST /api/admin/upload and calls onUploaded(absoluteUrl) once per file.
 *
 * Props:
 *  - onUploaded(url): callback fired for each successful file (URL is absolute)
 *  - multiple: allow selecting/dropping multiple files at once
 *  - disabled: disable the dropzone
 *  - label: primary label text
 *  - testId: data-testid on the dropzone element
 *  - compact: smaller variant
 *  - cropToRatio: number (e.g. 16/9) — client-side auto center-crop before upload
 */
export default function ImageDropzone({
    onUploaded,
    multiple = false,
    disabled = false,
    label = "Drag & drop image, or click to upload",
    testId,
    compact = false,
    cropToRatio,
}) {
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState("");

    const uploadOne = async (file) => {
        if (!file.type.startsWith("image/")) {
            toast.error(`${file.name}: not an image`);
            return false;
        }
        if (file.size > 15 * 1024 * 1024) {
            toast.error(`${file.name}: exceeds 15MB`);
            return false;
        }
        // Auto-crop client-side
        let out = file;
        if (cropToRatio) {
            try {
                setStatus("Cropping...");
                out = await cropImageToRatio(file, cropToRatio);
            } catch {
                toast.error(`${file.name}: could not crop, uploading original`);
                out = file;
            }
        }
        if (out.size > 15 * 1024 * 1024) {
            toast.error(`${file.name}: still exceeds 15MB after crop`);
            return false;
        }
        setStatus("Uploading...");
        const form = new FormData();
        form.append("file", out);
        try {
            const { data } = await api.post("/upload", form, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (e) => {
                    if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
                },
            });
            const absolute = data.url.startsWith("http") ? data.url : `${BACKEND}${data.url}`;
            onUploaded(absolute);
            return true;
        } catch (e) {
            toast.error(formatApiError(e));
            return false;
        }
    };

    const handleFiles = async (files) => {
        if (!files || !files.length || disabled) return;
        setUploading(true);
        setProgress(0);
        setStatus("");
        try {
            const list = Array.from(files);
            const items = multiple ? list : list.slice(0, 1);
            let ok = 0;
            for (const f of items) {
                setProgress(0);
                if (await uploadOne(f)) ok++;
            }
            if (multiple && items.length > 1) {
                toast.success(`Uploaded ${ok} of ${items.length}`);
            }
        } finally {
            setUploading(false);
            setProgress(0);
            setStatus("");
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        handleFiles(e.dataTransfer.files);
    };

    return (
        <label
            onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
            onDragEnter={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={`relative flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed text-center transition-all duration-200 select-none ${compact ? "p-4" : "p-8"} ${dragging ? "border-accent bg-accent/10 scale-[1.01]" : "border-border bg-secondary/40 hover:border-accent/60 hover:bg-accent/5"} ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            data-testid={testId}
        >
            <input
                ref={inputRef}
                type="file"
                className="hidden"
                accept="image/png,image/jpeg,image/webp,image/gif"
                multiple={multiple}
                disabled={disabled || uploading}
                onChange={(e) => handleFiles(e.target.files)}
            />
            {uploading ? (
                <>
                    <Loader2 className={`${compact ? "w-5 h-5" : "w-7 h-7"} text-accent animate-spin`} />
                    <span className="text-sm text-muted-foreground">{status || "Working..."}{progress ? ` ${progress}%` : ""}</span>
                </>
            ) : (
                <>
                    <UploadCloud className={`${compact ? "w-5 h-5" : "w-7 h-7"} text-accent`} />
                    <span className={`${compact ? "text-xs" : "text-sm"} font-medium`}>{label}</span>
                    <span className="text-[11px] text-muted-foreground">
                        PNG · JPG · WebP · GIF — up to 15MB, auto-compressed to ~250 KB {multiple ? "· multiple" : ""}
                        {cropToRatio ? " · auto-crop to 16:9" : ""}
                    </span>
                </>
            )}
        </label>
    );
}
